import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingBag, User, Phone, LogOut, Shield, Search } from "lucide-react";
import { SearchBar } from "@/components/SearchBar";
import { COMPANY } from "@/lib/legal";
import { openCookiePrefs } from "@/lib/consent";

export const LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_stamp-configurator/artifacts/eebff00e7d7bdbb6_HALFA%20STAMPS.webp";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";

const nav = [
  ["/catalogo", "Catalogo"],
  ["/catalogo?categoria=autoinchiostranti", "Autoinchiostranti"],
  ["/catalogo?categoria=rotondi", "Rotondi"],
  ["/catalogo?categoria=quadrati", "Quadrati"],
  ["/catalogo?categoria=gommini", "Gommini"],
  ["/catalogo?categoria=legno", "In legno"],
];

export function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [mSearch, setMSearch] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="bg-brand-navy text-brand-yellow text-[11px] font-mono tracking-wider">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-7 flex items-center justify-between">
          <span className="truncate">SPEDIZIONE GRATUITA DA 60 € · PRODUZIONE IN 24/48H</span>
          <a href="tel:+390547611342" className="hidden sm:flex items-center gap-1.5 hover:text-white" data-testid="header-phone-link"><Phone className="w-3 h-3" /> 0547 611342</a>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4 lg:gap-6">
        <Link to="/" className="flex items-center shrink-0" data-testid="header-logo-link"><img src={LOGO} alt="Halfa Stamps" className="h-11 w-auto" /></Link>
        <nav className="hidden xl:flex items-center gap-0.5 text-sm shrink-0">
          {nav.map(([to, l]) => (
            <NavLink key={l} to={to} className="px-3 py-2 rounded-md text-slate-700 hover:text-brand-navy hover:bg-brand-yellow/20 transition-colors" data-testid={`nav-${l.toLowerCase().replace(/\s/g, "-")}`}>{l}</NavLink>
          ))}
        </nav>
        <div className="hidden md:block flex-1 max-w-md ml-auto"><SearchBar /></div>
        <div className="ml-auto md:ml-0 flex items-center gap-1.5">
          <button data-testid="mobile-search-btn" onClick={() => setMSearch(!mSearch)} className="md:hidden press h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-100" aria-label="Cerca"><Search className="w-4 h-4" /></button>
          {user && user.role === "manager" && (
            <Link to="/manager" data-testid="header-manager-btn" className="press flex items-center gap-1.5 h-9 px-3 rounded-md bg-brand-navy text-white text-xs font-semibold"><Shield className="w-3.5 h-3.5" /><span className="hidden sm:inline">Pannello manager</span></Link>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="header-admin-btn" className="press hidden sm:flex items-center gap-1.5 h-9 px-3 rounded-md bg-brand-navy text-white text-xs font-semibold"><Shield className="w-3.5 h-3.5" /> Admin</Link>
          )}
          {user ? (
            <>
              <Link to="/account" data-testid="header-account-btn" className="press flex items-center gap-1.5 h-9 px-3 rounded-md hover:bg-slate-100 text-sm"><User className="w-4 h-4" /><span className="hidden lg:inline max-w-[120px] truncate">{user.name}</span></Link>
              <button data-testid="header-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="press h-9 w-9 flex items-center justify-center rounded-md hover:bg-slate-100" title="Esci"><LogOut className="w-4 h-4" /></button>
            </>
          ) : (
            user === false && (
              <span className="flex items-center h-9 px-2 rounded-md hover:bg-slate-100 text-sm whitespace-nowrap">
                <User className="w-4 h-4 mr-1.5 shrink-0" />
                <Link to="/accedi" data-testid="header-login-btn" className="hover:text-brand-navy hover:underline">Accedi</Link>
                <span className="mx-1 text-slate-400">/</span>
                <Link to="/accedi?registrati=1" data-testid="header-register-btn" className="hover:text-brand-navy hover:underline">Registrati</Link>
              </span>
            )
          )}
          <Link to="/carrello" data-testid="header-cart-btn" className="press relative flex items-center gap-1.5 h-9 px-3 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold hover:bg-brand-yellowdark">
            <ShoppingBag className="w-4 h-4" /> <span className="hidden sm:inline">Carrello</span>
            {count > 0 && <span data-testid="header-cart-count" className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-brand-navy text-white text-[10px] flex items-center justify-center">{count}</span>}
          </Link>
        </div>
      </div>
      {mSearch && <div className="md:hidden px-4 pb-3"><SearchBar mobile onDone={() => setMSearch(false)} /></div>}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-brand-navy text-slate-300 mt-24 border-t-4 border-brand-yellow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 md:grid-cols-4 text-sm">
        <div className="md:col-span-2 space-y-3">
          <div className="inline-block bg-white rounded-lg px-4 py-3"><img src={LOGO} alt="Halfa Stamps" className="h-12 w-auto" /></div>
          <p className="max-w-md text-slate-400">Timbri personalizzati online, prodotti a Cesena dal team di Halfa Timbri. Crea il tuo timbro con l'editor e ricevilo in pochi giorni in tutta Italia.</p>
        </div>
        <div className="space-y-2">
          <div className="font-mono text-[11px] tracking-widest text-slate-500">CONTATTI</div>
          <a href="tel:+390547611342" className="block hover:text-white">Tel. 0547 611342</a>
          <a href="https://wa.me/390547611342" target="_blank" rel="noreferrer" className="block hover:text-white" data-testid="footer-whatsapp-link">WhatsApp</a>
          <a href="https://www.halfatimbri.it" target="_blank" rel="noreferrer" className="block hover:text-white">halfatimbri.it</a>
          <a href="https://www.halfalaser.it" target="_blank" rel="noreferrer" className="block hover:text-white" data-testid="footer-halfalaser-link">halfalaser.it</a>
        </div>
        <div className="space-y-2">
          <div className="font-mono text-[11px] tracking-widest text-slate-500">NEGOZIO</div>
          <Link to="/catalogo" className="block hover:text-white">Catalogo timbri</Link>
          <Link to="/account" className="block hover:text-white">I miei ordini</Link>
          <Link to="/carrello" className="block hover:text-white">Carrello</Link>
          <Link to="/halfacard" className="block hover:text-white font-semibold text-brand-yellow" data-testid="footer-halfacard-link">HALFACARD · raccolta punti</Link>
          <div className="pt-3 font-mono text-[11px] tracking-widest text-slate-500">INFORMAZIONI LEGALI</div>
          <Link to="/termini" className="block hover:text-white" data-testid="footer-terms-link">Termini e condizioni</Link>
          <Link to="/recesso" className="block hover:text-white" data-testid="footer-refund-link">Recesso e rimborsi</Link>
          <Link to="/privacy" className="block hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/cookie-policy" className="block hover:text-white" data-testid="footer-cookie-link">Cookie Policy</Link>
          <button onClick={openCookiePrefs} className="block hover:text-white text-left" data-testid="footer-cookie-prefs-btn">Preferenze cookie</button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        <Link to="/recesso/modulo" data-testid="footer-withdraw-btn" className="press inline-flex items-center h-10 px-4 rounded-md border-2 border-brand-yellow text-brand-yellow text-sm font-semibold hover:bg-brand-yellow hover:text-brand-navy">Recedere dal contratto qui</Link>
      </div>
      <div className="border-t border-slate-800 text-[11px] font-mono text-slate-500 py-4 px-4 text-center">© {new Date().getFullYear()} Halfa Stamps · {COMPANY.owner} · {COMPANY.address} · P.IVA {COMPANY.vat} · REA {COMPANY.rea}</div>
    </footer>
  );
}
